import { useTracking } from '@folklore/tracking';
import classNames from 'classnames';
import uniq from 'lodash-es/uniq';
import { Fragment, startTransition, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useUpdateConsent, useCookiesSettings } from '../../contexts/ConsentContext';
import { useGlobalBrand } from '../../contexts/LayoutContext';
import { usePopupsContext } from '../../contexts/PopupsContext';
import { useSite, useSubscriptions } from '../../contexts/SiteContext';
import BasicButton from '../buttons/BasicButton';
import CookiesSettingsForm from '../forms/CookiesSettingsForm';
import IdentifyForm from '../forms/IdentifyForm';
import SubscriptionForm from '../forms/SubscriptionForm';
import LineSpacer from '../partials/LineSpacer';
import ModalDescription from '../typography/ModalDescription';
import ModalTitle from '../typography/ModalTitle';
import Dialog from './Dialog';
import Modal from './Modal';
import Notification from './Notification';

import styles from '../../styles/popups/consent-modal.module.css';

interface ConsentPromotion extends Promotion {
    cookiesDescription?: string | null;
    loginDescription?: string | null;
    subscribeButtonLabel?: string;
    cookiesButtonLabel?: string;
    sections?: string[];
    ssoOnly?: boolean;
    action?: 'subscription' | 'identify';
}

interface ConsentModalProps {
    brand?: string | null;
    promotion?: ConsentPromotion | null;
    title?: string | null;
    description?: string | null;
    cookiesDescription?: string | null;
    loginDescription?: string | null;
    className?: string | null;
    onClosed?: (() => void) | null;
}

function ConsentModal({
    brand = null,
    promotion = null,
    title: providedTitle = null,
    description: providedDescription = null,
    cookiesDescription: providedCookiesDescription = null,
    loginDescription: providedLoginDescription = null,
    className = null,
    onClosed = null,
}: ConsentModalProps) {
    const { handle: siteHandle } = useSite();
    const { handle: brandHandle = null } = useGlobalBrand();
    const allSubscriptions = useSubscriptions();
    const { handle: subscriptionHandle = null } =
        allSubscriptions.find(({ handle }) => handle === brandHandle) || {};

    const {
        placement = 'modal',
        title = providedTitle,
        description = providedDescription,
        cookiesDescription = providedCookiesDescription,
        loginDescription = providedLoginDescription,
        subscribeButtonLabel,
        cookiesButtonLabel,
        sections = ['login', 'cookies'],
        action = 'subscription',
        ssoOnly = false,
    } = promotion || {};

    const initialCookiesSettings = useCookiesSettings();
    const [cookieSettings, setCookieSettings] = useState<string[]>(initialCookiesSettings);
    const [opened, setOpened] = useState<boolean>(true);
    const tracking = useTracking();
    const updateConsent = useUpdateConsent();
    const [fieldHasValue, setFieldHasValue] = useState<boolean | null>(null);
    const { addMessage } = usePopupsContext();
    const isSubscription = action === 'subscription';
    const formRef = useRef<HTMLFormElement>(null);

    const consent = (type: string) => {
        updateConsent(true, cookieSettings);
        startTransition(() => {
            setOpened(false);
        });
        tracking.trackEventNow('Consent', 'given', type);
    };

    const onClickAccept = () => {
        if (fieldHasValue) {
            formRef.current?.submit();
            return;
        }
        consent('accept');
    };

    const onIdentify = ({ user = null, isNew = false }: { user?: User; isNew?: boolean }) => {
        consent('identify');
        addMessage('hi', {
            isNew,
            user,
        });
    };

    const onClickSso = (type: string) => {
        consent(`sso_${type}`);
    };

    const onFormChange = (newFormValue: { field?: string }) => {
        const { field = null } = newFormValue || {};
        setFieldHasValue(field !== null && field.length > 0);
    };

    useEffect(() => {
        tracking.trackEventNow('Consent', 'impression', placement);
    }, [placement]);

    const onRequestClose = () => {
        startTransition(() => {
            setOpened(false);
        });
    };

    const onCookieSettingsChange = (newValue: string[]) => {
        setCookieSettings(newValue);
    };
    const [cookiesSettingsOpened, setCookiesSettingsOpened] = useState<boolean>(false);
    const onClickCookiesSettings = () => {
        startTransition(() => {
            setCookiesSettingsOpened(!cookiesSettingsOpened);
        });
        tracking.trackEventNow('Consent', 'manage_cookies');
    };

    const cookies = (
        <div className={classNames([styles.section, styles.cookies])}>
            {cookiesDescription !== null ? (
                <ModalDescription
                    tag="div"
                    html={cookiesDescription}
                    className={classNames([styles.description])}
                />
            ) : null}
            <BasicButton
                className={styles.button}
                onClick={onClickAccept}
                disabled={cookieSettings === null || cookieSettings.length === 0}
            >
                {cookiesButtonLabel || (
                    <FormattedMessage
                        defaultMessage="Accepter les cookies et continuer"
                        description="Spacer label"
                    />
                )}
            </BasicButton>
            {cookiesSettingsOpened ? (
                <CookiesSettingsForm
                    value={cookieSettings}
                    onChange={onCookieSettingsChange}
                    className={styles.settingsForm}
                />
            ) : null}
            <div className={styles.links}>
                <BasicButton onClick={onClickCookiesSettings}>
                    {cookiesSettingsOpened ? (
                        <FormattedMessage
                            defaultMessage="Fermer les préférences de cookies"
                            description="Link to cookie settings"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Gérer les préférences de cookies"
                            description="Link to cookie settings"
                        />
                    )}
                </BasicButton>
                <a
                    href="https://www.iubenda.com/privacy-policy/49318682/cookie-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <FormattedMessage
                        defaultMessage="En savoir plus sur notre utilisation des cookies"
                        description="Link to cookie policy"
                    />
                </a>
            </div>
        </div>
    );

    const subscriptions = uniq([siteHandle, subscriptionHandle]);

    const login = (
        <div className={classNames([styles.section, styles.login])}>
            {loginDescription !== null ? (
                <ModalDescription
                    tag="div"
                    html={loginDescription}
                    className={classNames([styles.description])}
                />
            ) : null}
            {isSubscription ? (
                <SubscriptionForm
                    source={`consent-${placement}`}
                    subscription={subscriptions.length === 1 ? subscriptions[0] : null}
                    subscriptions={subscriptions.length > 1 ? subscriptions : null}
                    subscribeButtonLabel={subscribeButtonLabel || 'S’identifier'}
                    via={['email', 'sms']}
                    ssoTheme="dark"
                    ssoOnly={ssoOnly}
                    withoutEditContact
                    withoutMessages
                    className={styles.form}
                    onComplete={onIdentify}
                    onChange={onFormChange}
                    onClickSso={onClickSso}
                />
            ) : (
                <IdentifyForm
                    ref={formRef}
                    alwaysVertical
                    source={`consent-${placement}`}
                    className={styles.form}
                    onComplete={onIdentify}
                    onChange={onFormChange}
                    onClickSso={onClickSso}
                />
            )}
        </div>
    );

    const availableSections = {
        cookies,
        login,
    };

    const dialog = (
        <Dialog className={styles.dialog}>
            {title !== null || description !== null ? (
                <div className={classNames([styles.section, styles.main])}>
                    {title !== null ? <ModalTitle className={styles.title} html={title} /> : null}
                    {description !== null ? (
                        <ModalDescription
                            html={description}
                            className={styles.description}
                            tag="div"
                        />
                    ) : null}
                </div>
            ) : null}
            {sections.map((section, index) => (
                <Fragment key={section}>
                    {index > 0 ? (
                        <LineSpacer className={styles.spacer}>
                            <FormattedMessage defaultMessage="ou" description="Spacer label" />
                        </LineSpacer>
                    ) : null}
                    {availableSections[section]}
                </Fragment>
            ))}
        </Dialog>
    );

    if (placement === 'notification') {
        return (
            <Notification
                id="consent"
                opened={opened}
                withTransition
                requestClose={onRequestClose}
                withoutCloseButton
                className={classNames([
                    styles.container,
                    styles.notification,
                    styles[brand],
                    className,
                ])}
                onClosed={onClosed}
            >
                {dialog}
            </Notification>
        );
    }

    return (
        <Modal
            id="consent"
            opened={opened}
            withBackdrop
            withTransition
            withScrollLock
            requestClose={onRequestClose}
            className={classNames([styles.container, styles.modal, styles[brand], className])}
            onClosed={onClosed}
        >
            {dialog}
        </Modal>
    );
}

export default ConsentModal;
